import {
  CreditCard,
  Landmark,
  Wallet as WalletIcon,
  Shuffle,
  Globe,
  Coins,
  Layers,
  Palette,
  Store,
  Gauge,
  RefreshCw,
  Repeat,
} from "lucide-react";
import {
  PageHero,
  Container,
  SectionHead,
  SplitSection,
  FeatureGrid,
  CTASection,
  PrimaryButton,
  CheckList,
} from "@/components/common/Section";
import { Reveal } from "@/lib/anim";
import DashboardMock from "@/components/visuals/DashboardMock";
import ArchitectureDiagram from "@/components/visuals/ArchitectureDiagram";

const RAILS = [
  { icon: CreditCard, label: "Cards" },
  { icon: Landmark, label: "Bank Transfer" },
  { icon: WalletIcon, label: "Wallets" },
  { icon: Shuffle, label: "Alternative Payment Methods" },
  { icon: Globe, label: "Cross-Border" },
  { icon: Coins, label: "Multi-Currency" },
];

const FEATURES = [
  {
    icon: <Layers className="h-5 w-5" />,
    title: "Multi-tenancy",
    desc: "Run multiple merchants, brands and business lines on isolated tenants within a single deployment.",
  },
  {
    icon: <Palette className="h-5 w-5" />,
    title: "White-label experience",
    desc: "Multiple gateway themes and fully branded checkout, portal and communication surfaces.",
  },
  {
    icon: <Store className="h-5 w-5" />,
    title: "Merchant management",
    desc: "Onboard, configure and operate merchants with dashboards, settlements and reporting built in.",
  },
  {
    icon: <Repeat className="h-5 w-5" />,
    title: "Recurring payments",
    desc: "Subscriptions and repeat billing with configurable schedules, retries and notifications.",
  },
  {
    icon: <RefreshCw className="h-5 w-5" />,
    title: "Refunds & reversals",
    desc: "Full and partial refunds across rails, tracked end to end with audit visibility.",
  },
  {
    icon: <Gauge className="h-5 w-5" />,
    title: "Routing & orchestration",
    desc: "Route transactions across gateways and acquirers with configurable, tenant-level rules.",
  },
];

const SECURITY_POINTS = [
  "PCI-certified secure platform",
  "Encryption of sensitive payment data",
  "Role-based access control",
  "Controlled API gateway connectivity",
  "Comprehensive audit logging",
  "Platform and transaction monitoring",
  "Built with financial industry requirements in mind",
];

export default function ZenPay() {
  return (
    <>
      <PageHero
        eyebrow="ZenPay"
        title="Neo-banking infrastructure with payments at its core."
        copy="ZenPay is iJeeva's PCI-certified, multi-tenant neo-banking platform: multi-currency accounts, payouts and a white-label payment gateway, delivered through one secure platform."
        testid="zenpay-page-title"
      >
        <Reveal delay={120} className="mt-9 flex flex-col gap-4 sm:flex-row">
          <PrimaryButton to="/contact" testid="zenpay-hero-cta">
            Talk to Our Payments Team
          </PrimaryButton>
        </Reveal>
        <Reveal delay={200} className="mt-6 flex flex-wrap gap-2.5">
          {RAILS.map((r) => (
            <span
              key={r.label}
              data-testid={`zenpay-rail-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-[13px] font-semibold text-[#1D305B]"
            >
              <r.icon className="h-4 w-4 text-[#2563EB]" aria-hidden="true" />
              {r.label}
            </span>
          ))}
        </Reveal>
      </PageHero>

      <section className="py-20 lg:py-24">
        <Container>
          <SplitSection
            eyebrow="Product Overview"
            title="A banking and payments platform you can make your own."
            copy="ZenPay combines neo-banking capabilities with a full-featured payment gateway: multi-currency accounts, cards, bank transfers, wallets and alternative payment methods through one API-driven, PCI-certified platform."
            points={[
              "Multi-currency accounts and settlement",
              "Payment gateway with smart routing",
              "Recurring payments and retries",
              "Refunds across connected rails",
              "Card acquiring connectivity",
              "White-label themes for every tenant",
            ]}
            visual={<DashboardMock />}
          />
          <p className="mt-6 text-xs text-[#94A3B8]">Dashboard shown with representative demo data.</p>
        </Container>
      </section>

      <section className="border-y border-[#E2E8F0] bg-white py-20 lg:py-24">
        <Container>
          <SectionHead
            eyebrow="Capabilities"
            title="Built for payment operators."
            copy="The operational surface payment providers, aggregators and enterprises need to run payments as a business."
          />
          <div className="mt-14">
            <FeatureGrid items={FEATURES} testidPrefix="zenpay-feature" />
          </div>
        </Container>
      </section>

      <section className="bg-[#1D305B] py-20 lg:py-24">
        <Container className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#5EEAD4]">
              <span className="h-px w-6 bg-[#5EEAD4]/60" aria-hidden="true" />
              Security
            </span>
            <h2 className="mt-4 text-[30px] font-semibold leading-[1.15] tracking-tight text-white sm:text-4xl">
              Security is engineered into the platform.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#94A3B8] sm:text-lg">
              ZenPay is designed with security, control and auditability at every layer of the payment flow.
            </p>
            <div className="mt-7 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.05] px-5 py-4">
              <img src="/pci-ssc-white.png" alt="PCI Security Standards Council" className="h-9 w-auto" />
              <p className="text-sm font-medium leading-snug text-[#CBD5E1]">
                PCI-certified
                <br className="hidden sm:block" /> secure platform
              </p>
            </div>
          </Reveal>
          <Reveal delay={140}>
            <CheckList dark twoCol items={SECURITY_POINTS} />
          </Reveal>
        </Container>
      </section>

      <section className="py-20 lg:py-24">
        <Container>
          <SectionHead
            center
            eyebrow="Architecture"
            title="API-first. Multi-tenant. Extensible."
            copy="ZenPay runs on iJeeva's reference payment architecture, with 60+ pre-built connectors for gateways, banks, KYC, fraud and FX providers."
          />
          <div className="mt-14">
            <ArchitectureDiagram />
          </div>
        </Container>
      </section>

      <CTASection
        title="Evaluate ZenPay for your payment business."
        copy="Tell us about your rails, merchants and markets, and we'll walk you through the platform."
        secondary={{ to: "/platforms", label: "Explore Our Platforms", testid: "zenpay-cta-platforms" }}
      />
    </>
  );
}
